import React, { useState } from 'react';
import { Upload, X, FileText } from 'lucide-react';
import CustomSelect from '../../../ui/CustomSelect';

interface PersonalTabErrors {
  full_name?: string;
  nik?: string;
  email?: string;
}

interface PersonalTabProps {
  formData: any;
  handleChange: (field: string, value: string) => void;
  errors?: PersonalTabErrors;
}

export default function PersonalTab({ formData, handleChange, errors = {} }: PersonalTabProps) {
  const [previewUrl, setPreviewUrl] = useState(formData.photo_url || null);
  const [photoError, setPhotoError] = useState('');
  const [ktpFile, setKtpFile] = useState<File | null>(null);
  const [ktpPreviewUrl, setKtpPreviewUrl] = useState(formData.ktp_url || null);
  const [ktpError, setKtpError] = useState('');
  const [isKtpUploading, setIsKtpUploading] = useState(false);
  const [isKtpRemoving, setIsKtpRemoving] = useState(false);

  const handlePhotoChange = e => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setPhotoError('Ukuran file maksimal 2MB');
        e.target.value = '';
        return;
      }
      setPhotoError('');
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          setPreviewUrl(reader.result);
          handleChange('photo_url', reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleKtpChange = e => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setKtpError('Ukuran file maksimal 5MB');
        e.target.value = '';
        return;
      }
      setKtpError('');
      setIsKtpUploading(true);
      setKtpFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          setKtpPreviewUrl(reader.result);
          handleChange('ktp_url', reader.result);
          // Animation delay
          setTimeout(() => {
            setIsKtpUploading(false);
          }, 300);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeKtpFile = () => {
    setIsKtpRemoving(true);
    setTimeout(() => {
      setKtpFile(null);
      setKtpPreviewUrl(null);
      handleChange('ktp_url', '');
      setKtpError('');
      setIsKtpRemoving(false);
      // Reset file input
      const fileInput = document.querySelector(
        'input[type="file"][accept*="image"]'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }, 300);
  };

  const isImageFile = (file: File | null) => {
    if (!file) return false;
    return file.type.startsWith('image/');
  };

  const isPdfFile = (file: File | null) => {
    if (!file) return false;
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Data Pribadi</h3>
        <p className="text-gray-500 text-sm">Informasi identitas dan kontak pegawai</p>
      </div>

      <div className="flex items-center gap-6">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Preview"
            className="w-24 h-24 rounded-full object-cover border-2 border-emerald-200"
          />
        ) : (
          <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 text-2xl font-bold">
            {formData.full_name?.charAt(0) || 'U'}
          </div>
        )}
        <div>
          <label className="cursor-pointer">
            <div className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 hover:border-emerald-500 transition-all duration-300">
              <Upload className="w-4 h-4" />
              Upload Foto
            </div>
            <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
          </label>
          {photoError ? (
            <p className="text-xs text-red-500 mt-1">{photoError}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">JPG, PNG max 2MB</p>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nama Lengkap <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.full_name || ''}
            onChange={e => handleChange('full_name', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:border-emerald-500 transition-colors duration-200 ${
              errors.full_name ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.full_name && <p className="text-sm text-red-600 mt-1">{errors.full_name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">NIK</label>
          <input
            type="text"
            value={formData.nik || ''}
            onChange={e => {
              // Only allow numeric input and limit to 16 digits
              let value = e.target.value.replace(/\D/g, '');
              if (value.length > 16) {
                value = value.slice(0, 16);
              }
              handleChange('nik', value);
            }}
            placeholder="16 digit nomor"
            maxLength={16}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:border-emerald-500 transition-colors duration-200 ${
              errors.nik ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.nik && <p className="text-sm text-red-600 mt-1">{errors.nik}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tempat Lahir</label>
          <input
            type="text"
            value={formData.birth_place || ''}
            onChange={e => handleChange('birth_place', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Lahir</label>
          <input
            type="date"
            value={
              formData.birth_date
                ? typeof formData.birth_date === 'string' && formData.birth_date.includes('T')
                  ? formData.birth_date.split('T')[0]
                  : formData.birth_date
                : ''
            }
            onChange={e => handleChange('birth_date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Kelamin</label>
          <CustomSelect
            value={formData.gender || ''}
            onChange={v => handleChange('gender', v)}
            placeholder="Pilih"
            width="w-full"
            options={[
              { value: 'male', label: 'Laki-laki' },
              { value: 'female', label: 'Perempuan' },
            ]}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status Pernikahan</label>
          <CustomSelect
            value={formData.marital_status || ''}
            onChange={v => handleChange('marital_status', v)}
            placeholder="Pilih status"
            width="w-full"
            options={[
              { value: 'single', label: 'Belum Menikah' },
              { value: 'married', label: 'Menikah' },
              { value: 'divorced', label: 'Cerai' },
              { value: 'widowed', label: 'Janda/Duda' },
            ]}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">No. Telepon / WA</label>
          <input
            type="text"
            value={formData.phone || ''}
            onChange={e => {
              let value = e.target.value;
              // Limit to 50 characters to match database constraint
              if (value.length > 50) {
                value = value.slice(0, 50);
              }
              handleChange('phone', value);
            }}
            maxLength={50}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Pribadi <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.email || ''}
            onChange={e => handleChange('email', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:border-emerald-500 transition-colors duration-200 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Alamat Domisili</label>
        <textarea
          value={formData.address || ''}
          onChange={e => handleChange('address', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nama Kontak Darurat
          </label>
          <input
            type="text"
            value={formData.emergency_contact_name || ''}
            onChange={e => handleChange('emergency_contact_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">No. Kontak Darurat</label>
          <input
            type="text"
            value={formData.emergency_contact_phone || ''}
            onChange={e => {
              let value = e.target.value;
              // Limit to 50 characters to match database constraint
              if (value.length > 50) {
                value = value.slice(0, 50);
              }
              handleChange('emergency_contact_phone', value);
            }}
            maxLength={50}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Upload KTP</label>
        {ktpPreviewUrl && !isKtpRemoving ? (
          <div className={`space-y-2 ${isKtpUploading ? 'animate-imageIn' : ''}`}>
            {isImageFile(ktpFile) ? (
              <div
                className={`relative border-2 border-gray-200 rounded-md overflow-hidden ${isKtpUploading ? 'animate-imageIn' : ''}`}
              >
                <img
                  src={ktpPreviewUrl}
                  alt="KTP Preview"
                  className="w-full h-auto max-h-96 object-contain"
                />
                <button
                  type="button"
                  onClick={removeKtpFile}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 transition-all duration-200 shadow-lg hover:scale-110"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : isPdfFile(ktpFile) ? (
              <div
                className={`relative border-2 border-gray-200 rounded-md p-4 bg-gray-50 ${isKtpUploading ? 'animate-imageIn' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-10 h-10 text-red-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {ktpFile?.name || 'KTP.pdf'}
                      </p>
                      <p className="text-xs text-gray-500">PDF Document</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeKtpFile}
                    className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-200 hover:scale-110"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-4">
                  <a
                    href={ktpPreviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-all duration-200 text-sm hover:scale-105"
                  >
                    <FileText className="w-4 h-4" />
                    Buka PDF
                  </a>
                </div>
              </div>
            ) : (
              <div
                className={`border-2 border-gray-200 rounded-md p-4 bg-gray-50 ${isKtpUploading ? 'animate-imageIn' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-10 h-10 text-gray-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">{ktpFile?.name || 'File'}</p>
                      <p className="text-xs text-gray-500">File terupload</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeKtpFile}
                    className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-200 hover:scale-110"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={isKtpRemoving ? 'animate-imageOut' : ''}>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={handleKtpChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm transition-all duration-200"
            />
          </div>
        )}
        {ktpError && <p className="text-xs text-red-500 mt-1 animate-fadeIn">{ktpError}</p>}
      </div>
    </div>
  );
}
